import type { AuditEvent } from "../../api/types";

/**
 * ADR-0058's governed-promotion gate records ONE fact server-side per decision: a
 * `governance.decision` [AuditEvent] (`auditGovernanceDecision`,
 * server/internal/projectsvc/promote.go). That row is the single source of truth (A-5)
 * — this module derives a GOVERNANCE-shaped projection of it for the Governance
 * Decisions view; it never duplicates or re-authors the decision, only reads it.
 *
 * The audit row already carries three fields structurally (never string-parsed):
 *   - `outcome`   → the verdict ("allow" | "deny", `audit.OutcomeAllow`/`OutcomeDeny`)
 *   - `target`    → the service id being promoted
 *   - `actorUserId`/`actorRole` → the principal
 *
 * The rest — source/target ENVIRONMENT, the pinned image, the governance commit, and
 * the fired rule ids — are recorded only inside `detail`'s free-text summary (the audit
 * vocabulary is a bounded, non-secret string by design, S-13/O-1), formatted EXACTLY by
 * `auditGovernanceDecision`:
 *
 *   governance {allow|deny} promote from={srcEnvId} to={tgtEnvId} image={ref} commit={sha} rules=[{r1, r2}]
 *
 * `parseGovernanceDetail` decodes that one format. A row whose `detail` doesn't match —
 * a version-skewed server, a future format change (A-3) — still surfaces honestly: the
 * caller keeps the structural fields (verdict/service/actor/time) and renders the raw
 * `detail` text instead of fabricating source/target/image/commit (A-10).
 */
const DETAIL_PATTERN =
  /^governance (?:allow|deny) promote from=(\S+) to=(\S+) image=(\S+) commit=(\S+) rules=\[(.*)\]$/;

export interface ParsedGovernanceDetail {
  sourceEnvId: string;
  targetEnvId: string;
  image: string;
  governanceCommit: string;
  /** The declared/floor predicate id(s) that produced this verdict — empty on an
   * allow (no predicate fired). Dotted ids, e.g. "promote.require.source_class". */
  firedRules: string[];
}

/** Parses one `governance.decision` audit event's `detail` string. Returns `null` when
 * it doesn't match the known format (A-3/A-10: never guess at a shape that changed
 * server-side) — the caller falls back to the raw text rather than showing wrong data. */
export function parseGovernanceDetail(detail: string): ParsedGovernanceDetail | null {
  const m = DETAIL_PATTERN.exec(detail.trim());
  if (!m) return null;
  const [, sourceEnvId, targetEnvId, image, governanceCommit, rulesRaw] = m;
  const firedRules = rulesRaw.trim().length > 0 ? rulesRaw.split(",").map((r) => r.trim()).filter(Boolean) : [];
  return { sourceEnvId, targetEnvId, image, governanceCommit, firedRules };
}

/** A governance verdict, mirroring `governancesvc.Verdict` — but read off the audit
 * row's own `outcome` field (already structural), never re-derived from `detail`. An
 * `AuditEvent.outcome` this action never actually writes (version skew) renders
 * "unknown" rather than defaulting to either alarm state (A-10 fail-closed-to-honest,
 * not fail-closed-to-deny — this is a READ surface, not an enforcement point). */
export type GovernanceVerdict = "allow" | "deny" | "unknown";

export function governanceVerdict(outcome: string): GovernanceVerdict {
  if (outcome === "allow") return "allow";
  if (outcome === "deny") return "deny";
  return "unknown";
}

/** One governance decision as the Governance Decisions view renders it: the audit
 * event's own structural fields, plus the parsed `detail` fields when they decode. */
export interface GovernanceDecision {
  id: string;
  occurredAt: string;
  verdict: GovernanceVerdict;
  serviceId: string;
  actorUserId?: string;
  actorRole?: string;
  /** `undefined` when `detail` didn't match the known format — render the raw text
   * fallback instead (see `rawDetail`), never a fabricated source/target/image/commit. */
  parsed?: ParsedGovernanceDetail;
  /** The original `detail` string — always kept, so an unparseable row still shows
   * SOMETHING concrete rather than going blank (A-10). */
  rawDetail: string;
}

/** Projects a raw `governance.decision` [AuditEvent] into a [GovernanceDecision]. The
 * caller is expected to have already filtered to `action === "governance.decision"`
 * (the list query does this server-side, see `useGovernanceDecisions`) — this function
 * does not re-check the action, it only re-shapes one row. */
export function toGovernanceDecision(event: AuditEvent): GovernanceDecision {
  const parsed = parseGovernanceDetail(event.detail ?? "");
  return {
    id: event.id,
    occurredAt: event.occurredAt,
    verdict: governanceVerdict(event.outcome),
    serviceId: event.target ?? "",
    actorUserId: event.actorUserId,
    actorRole: event.actorRole,
    parsed: parsed ?? undefined,
    rawDetail: event.detail ?? "",
  };
}
